import { Trans } from '@lingui/react/macro'
import { useJackpotsQuery } from '@/features/jackpots/use-jackpots-query/use-jackpots-query'
import { getGamesWithJackpotValue } from '@/features/jackpots/get-games-with-jackpot-value/get-games-with-jackpot-value'
import { useEffect, useMemo } from 'react'
import { LayoutItemCard } from '@/features/category/layout-factory/layout-item-card'
import { useGameInfoDrawerLaunch } from '@/features/game-info-drawer/use-game-info-drawer-launch/use-game-info-drawer-launch'
import { HeroBanner } from '@/features/hero-banner/hero-banner'
import { useWalletStore } from '@/features/wallet-store/use-wallet-store'
import { notFound, createFileRoute, useLocation } from '@tanstack/react-router'
import { LobbyRootLayout } from '../../-lobby-root-layout'
import { assertCanPlayBeforeLoad } from '@/features/ctrls-and-caps/assert-can-play-before-load'
import { useCategoryQuery } from '@/features/categories-api/use-category-query/use-category-query'
import { useGameAccessAttemptedTracking } from '@/features/game-tracking/use-game-access-attempted-tracking'
import { useCategoryPageTracking } from '@/features/category/use-category-page-tracking'

export const Route = createFileRoute('/_app/category/$id')({
  component: CategoryPage,
  beforeLoad: async () => {
    await assertCanPlayBeforeLoad()
  },
})

function CategoryPage() {
  const { id } = Route.useParams()
  const coinType = useWalletStore((state) => state.coinType)
  const { state } = useLocation()
  const { trackCategory } = useCategoryPageTracking()
  const { data: jackpots } = useJackpotsQuery()
  const { isLoading, notFoundError, category, error } = useCategoryQuery({
    categoryId: id,
  })

  const { trackGameCardAccessed } = useGameAccessAttemptedTracking()

  const { open: openGameInfo } = useGameInfoDrawerLaunch()

  useEffect(() => {
    if (notFoundError) {
      throw notFound()
    }
  }, [notFoundError])

  useEffect(() => {
    if (!state?.from && category?.name) {
      trackCategory({
        categoryId: id,
        categoryName: category?.name,
        categoryAccessPoint: 'deeplink',
      })
    }
  }, [trackCategory, id, category?.name, state?.from])

  const games = useMemo(() => {
    if (!category?.games) {
      return []
    }
    return getGamesWithJackpotValue(
      'portrait',
      category.games,
      coinType,
      jackpots,
    )
  }, [category?.games, coinType, jackpots])

  if (isLoading) {
    return (
      <LobbyRootLayout>
        <span data-testid="category-loading">
          <Trans>Loading</Trans>
        </span>
      </LobbyRootLayout>
    )
  }

  if (error && !notFoundError) {
    return (
      <LobbyRootLayout>
        <p data-testid="category-error">
          <Trans>An error has occurred</Trans>
        </p>
      </LobbyRootLayout>
    )
  }

  if (!category) {
    return null
  }

  return (
    <LobbyRootLayout>
      <section
        className="flex w-full flex-col px-2 pt-6 pb-2 sm:px-4 sm:pb-4 md:px-6 md:pb-6"
        data-testid="category-page"
      >
        <header className="mb-2 font-display text-xl font-bold text-category-content-category-label md:text-2xl">
          <h3>{category.name}</h3>
        </header>
        <div className="flex flex-col gap-2 sm:gap-4 lg:gap-6">
          {!!category.isMarketingBannerVisible && (
            <HeroBanner
              page={id}
              placement="subpage"
              pageType="category"
              showDefault={false}
            />
          )}
          <div className="grid grid-cols-2 gap-2 sm:gap-4 md:grid-cols-3 lg:gap-6 xl:grid-cols-4 2xl:grid-cols-6">
            {games.map((game) => (
              <LayoutItemCard
                onMoreInfo={() => {
                  if (game.gameId && category.name && category.categoryId) {
                    openGameInfo({
                      gameId: game.gameId,
                      categoryId: category.categoryId,
                    })
                  }
                }}
                key={game.gameId}
                name={game?.name}
                imageUrl={game?.thumbnails?.image2x3}
                gameId={game.gameId}
                launchUrl={game?.launchUrl}
                availability={game?.availability}
                variant="portrait"
                jackpots={game.jackpots}
                onClick={() => {
                  trackGameCardAccessed({
                    gameId: game.gameId,
                    gameName: game.name,
                    gameCategoryName: category.name,
                    categoryId: category.categoryId,
                    // eslint-disable-next-line lingui/no-unlocalized-strings
                    gameEntryPoint: 'category page',
                  })
                }}
              />
            ))}
          </div>
        </div>
      </section>
    </LobbyRootLayout>
  )
}
